/**
 * Bundle Builder for Bohomoon
 * Ported from Universal Colours bundle builder, adapted for
 * Bohomoon's theme patterns, step-based navigation, and cart events.
 */
(() => {
  'use strict';

  class BundleBuilder {
    constructor(element) {
      this.element = element;
      const config = window.BundleBuilderConfig || {};

      this.config = {
        sectionId: config.sectionId || element.dataset.sectionId,
        bundleMin: parseInt(config.bundleMin) || parseInt(element.dataset.bundleMin) || 4,
        bundleMax: parseInt(config.bundleMax) || parseInt(element.dataset.bundleMax) || 12,
        bundleTagKey: config.bundleTagKey || element.dataset.bundleTagKey || 'bundle',
        bundleTagValue: config.bundleTagValue || element.dataset.bundleTagValue || 'BOHOBUNDLE',
        discountPercentage: parseInt(config.discountPercentage) || parseInt(element.dataset.discountPercentage) || 20,
        discountMessage: config.discountMessage || 'Save on your bundle!',
        steps: config.steps || [],
        moneyFormat: config.moneyFormat || '£{{amount}}',
        rootUrl: config.rootUrl || '',
        cartAddUrl: config.cartAddUrl || '/cart/add.js',
        cartUrl: config.cartUrl || '/cart.js',
        productsPerPage: parseInt(config.productsPerPage) || 6,
        colorLabelList: (config.colorLabelList || ['color', 'colour']).map(s => s.toLowerCase()),
        sizeLabelList: (config.sizeLabelList || ['size']).map(s => s.toLowerCase()),
        colorSwatchConfig: this.parseColorSwatchConfig(config.colorSwatchConfig || []),
        currencySymbol: config.currencySymbol || '£',
        translations: config.translations || {}
      };

      // State
      this.state = {
        activeStep: 0,
        selectedProducts: new Map(),
        isLoading: false,
        activeFilters: new Map(), // Map<paramName, Set<string>> for list filters, or { min, max } for price_range
        visibleCounts: {},
        fetchedSteps: new Set([0]) // Step 0 is server-rendered
      };

      // Cache DOM elements
      this.els = {
        stepButtons: element.querySelectorAll('.bundle-step-nav__button'),
        grids: element.querySelectorAll('[data-step-grid]'),
        // Sidebar (desktop)
        sidebarCount: element.querySelector('[data-sidebar-count]'),
        progressFill: element.querySelector('[data-sidebar-progress]'),
        progressTrack: element.querySelector('[data-sidebar-progress-track]'),
        sidebarItems: element.querySelector('[data-sidebar-items]'),
        sidebarItemsWrapper: element.querySelector('[data-sidebar-items-wrapper]'),
        sidebarPricing: element.querySelector('[data-sidebar-pricing]'),
        sidebarSubtotal: element.querySelector('[data-sidebar-subtotal]'),
        sidebarDiscountRow: element.querySelector('[data-sidebar-discount-row]'),
        sidebarDiscountAmount: element.querySelector('[data-sidebar-discount-amount]'),
        sidebarTotal: element.querySelector('[data-sidebar-total]'),
        discountMsg: element.querySelector('[data-discount-msg]'),
        savingsMsg: element.querySelector('[data-sidebar-savings]'),
        ctaBtn: element.querySelector('[data-bundle-add-to-cart]'),
        // Mobile bottom sheet
        bottomSheet: element.querySelector('[data-bundle-bottom-sheet]'),
        bottomSheetBar: element.querySelector('[data-bottom-sheet-bar]'),
        bottomSheetToggle: element.querySelector('[data-bottom-sheet-toggle]'),
        bottomSheetContent: element.querySelector('[data-bottom-sheet-content]'),
        mobileSheetCount: element.querySelector('[data-mobile-sheet-count]'),
        mobileSheetSubtotal: element.querySelector('[data-mobile-sheet-subtotal]'),
        mobileSheetItems: element.querySelector('[data-mobile-sheet-items]'),
        mobileSheetPricing: element.querySelector('[data-mobile-sheet-pricing]'),
        mobileSubtotal: element.querySelector('[data-mobile-subtotal]'),
        mobileDiscountRow: element.querySelector('[data-mobile-discount-row]'),
        mobileDiscountAmount: element.querySelector('[data-mobile-discount-amount]'),
        mobileTotal: element.querySelector('[data-mobile-total]'),
        mobileCta: element.querySelector('[data-mobile-add-to-cart]'),
        mobileSavings: element.querySelector('[data-mobile-savings]'),
        // Step heading + controls
        stepHeading: element.querySelector('[data-step-heading]'),
        stepPrev: element.querySelector('[data-step-prev]'),
        stepNext: element.querySelector('[data-step-next]'),
        loadMoreBtns: element.querySelectorAll('[data-load-more]'),
        // Filter drawer
        filterToggle: element.querySelector('[data-filter-toggle]'),
        filterDrawer: document.querySelector('[data-filter-drawer]'),
        filterOverlay: document.querySelector('[data-filter-overlay]'),
        filterCloseBtns: document.querySelectorAll('[data-filter-close]'),
        filterSections: document.querySelector('[data-filter-sections]'),
        filterBadge: document.querySelector('[data-filter-badge]'),
        filterBtnBadge: element.querySelector('[data-filter-btn-badge]'),
        filterClearAll: document.querySelector('[data-filter-clear-all]'),
        // Quick-view modal
        quickView: document.querySelector('[data-bundle-quick-view]'),
        quickViewImage: document.querySelector('[data-quick-view-image]'),
        quickViewThumbs: document.querySelector('[data-quick-view-thumbs]'),
        quickViewTitle: document.querySelector('[data-quick-view-title]'),
        quickViewPrice: document.querySelector('[data-quick-view-price]'),
        quickViewSwatches: document.querySelector('[data-quick-view-swatches]'),
        quickViewDescription: document.querySelector('[data-quick-view-description]'),
        quickViewAdd: document.querySelector('[data-quick-view-add]'),
        quickViewCloseBtns: document.querySelectorAll('[data-quick-view-close]'),
        // Announcement
        announcement: element.querySelector('[data-bundle-announcement]')
      };

      this.init();
    }

    init() {
      this.setupEventListeners();
      this.setActiveStep(0);
      this.updateUI();
      this.measureStepNav();
    }

    /**
     * Measure the step nav height and set a CSS variable so the
     * sticky sidebar clears it.
     */
    measureStepNav() {
      const nav = this.element.querySelector('.bundle-step-nav');
      if (!nav) return;
      const update = () => {
        this.element.style.setProperty('--bb-nav-height', `${nav.offsetHeight}px`);
      };
      update();
      window.addEventListener('resize', update, { passive: true });
    }

    // ─── Event listeners ───────────────────────────────────────

    setupEventListeners() {
      // Step nav
      this.els.stepButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.setActiveStep(parseInt(btn.dataset.stepIndex));
        });
      });

      // Delegated click handler on product grid
      this.element.addEventListener('click', (e) => {
        // Toggle button (add/remove)
        const toggle = e.target.closest('[data-bundle-toggle]');
        if (toggle) {
          e.preventDefault();
          const card = toggle.closest('.bundle-product-card');
          if (card) this.handleToggle(card);
          return;
        }

        // Info button — open quick-view drawer
        const infoBtn = e.target.closest('.bundle-product-card__info-btn');
        if (infoBtn) {
          e.preventDefault();
          const url = infoBtn.dataset.productUrl;
          if (url) this.openQuickView(url);
          return;
        }

        // Sidebar remove button
        const removeBtn = e.target.closest('[data-remove-selection]');
        if (removeBtn) {
          e.preventDefault();
          this.removeProduct(removeBtn.dataset.removeSelection);
          return;
        }

        // Qty minus button
        const minusBtn = e.target.closest('[data-qty-minus]');
        if (minusBtn) {
          e.preventDefault();
          const card = minusBtn.closest('.bundle-product-card');
          if (card) this.adjustCardQty(card, -1);
          return;
        }

        // Qty plus button
        const plusBtn = e.target.closest('[data-qty-plus]');
        if (plusBtn) {
          e.preventDefault();
          const card = plusBtn.closest('.bundle-product-card');
          if (card) this.adjustCardQty(card, 1);
          return;
        }

        // Colour swatch clicks (label triggers radio change)
        const swatchLabel = e.target.closest('.bundle-product-card__swatch');
        if (swatchLabel) {
          const card = swatchLabel.closest('.bundle-product-card');
          const radioInput = swatchLabel.previousElementSibling;
          if (card && radioInput) this.handleSwatchClick(card, radioInput);
          return;
        }
      });

      // Qty input — clamp on every keystroke (input) AND on blur (change)
      const clampQtyInput = (qtyInput) => {
        const card = qtyInput.closest('.bundle-product-card');
        if (!card || card.classList.contains('is-selected')) return;
        let val = parseInt(qtyInput.value) || 1;
        const max = parseInt(qtyInput.max) || 1;
        val = Math.max(1, Math.min(max, val));
        qtyInput.value = val;
        const minus = card.querySelector('[data-qty-minus]');
        const plus = card.querySelector('[data-qty-plus]');
        if (minus) minus.disabled = val <= 1;
        if (plus) plus.disabled = val >= max;
      };

      this.element.addEventListener('input', (e) => {
        const qtyInput = e.target.closest('[data-qty-input]');
        if (qtyInput) clampQtyInput(qtyInput);
      });

      // Size dropdown + qty input changes (delegated)
      this.element.addEventListener('change', (e) => {
        const qtyInput = e.target.closest('[data-qty-input]');
        if (qtyInput) {
          clampQtyInput(qtyInput);
          return;
        }

        // Size dropdown changes
        const sizeDropdown = e.target.closest('[data-size-dropdown]');
        if (sizeDropdown) {
          const card = sizeDropdown.closest('.bundle-product-card');
          if (card) this.handleSizeChange(card, sizeDropdown);
        }
      });

      // CTA buttons
      if (this.els.ctaBtn) {
        this.els.ctaBtn.addEventListener('click', () => this.addToCart());
      }
      if (this.els.mobileCta) {
        this.els.mobileCta.addEventListener('click', () => this.addToCart());
      }

      // Filter drawer
      if (this.els.filterToggle) {
        this.els.filterToggle.addEventListener('click', () => this.openFilterDrawer());
      }
      if (this.els.filterCloseBtns) {
        this.els.filterCloseBtns.forEach(btn => {
          btn.addEventListener('click', () => this.closeFilterDrawer());
        });
      }
      if (this.els.filterOverlay) {
        this.els.filterOverlay.addEventListener('click', () => this.closeFilterDrawer());
      }
      if (this.els.filterClearAll) {
        this.els.filterClearAll.addEventListener('click', () => this.clearAllFilters());
      }

      // Quick-view modal
      if (this.els.quickViewCloseBtns) {
        this.els.quickViewCloseBtns.forEach(btn => {
          btn.addEventListener('click', () => this.closeQuickView());
        });
      }
      if (this.els.quickViewAdd) {
        this.els.quickViewAdd.addEventListener('click', () => this.addFromQuickView());
      }

      // Escape key closes quick-view and filter drawer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this.els.quickView && this.els.quickView.classList.contains('is-open')) {
            this.closeQuickView();
          } else if (this.els.filterDrawer && this.els.filterDrawer.classList.contains('is-open')) {
            this.closeFilterDrawer();
          }
        }
      });

      // Bottom sheet toggle
      if (this.els.bottomSheetToggle) {
        this.els.bottomSheetToggle.addEventListener('click', () => this.toggleBottomSheet());
      }

      // Prev/Next step buttons
      if (this.els.stepPrev) {
        this.els.stepPrev.addEventListener('click', () => {
          if (this.state.activeStep > 0) {
            this.setActiveStep(this.state.activeStep - 1);
          }
        });
      }
      if (this.els.stepNext) {
        this.els.stepNext.addEventListener('click', () => {
          if (this.state.activeStep < this.config.steps.length - 1) {
            this.setActiveStep(this.state.activeStep + 1);
          }
        });
      }

      // Load More buttons
      this.els.loadMoreBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const stepIndex = parseInt(btn.dataset.loadMore);
          this.loadMore(stepIndex);
        });
      });
    }

    // ─── Step navigation ───────────────────────────────────────

    async setActiveStep(index) {
      this.state.activeStep = index;
      const step = this.config.steps[index];
      if (!step) return;

      // Update nav buttons
      this.els.stepButtons.forEach(btn => {
        const isActive = parseInt(btn.dataset.stepIndex) === index;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive);
      });

      // Toggle step grids — show active, hide others
      this.els.grids.forEach(grid => {
        const gridIndex = parseInt(grid.dataset.stepGrid);
        grid.style.display = gridIndex === index ? '' : 'none';
      });

      // Fetch step content if not already loaded
      await this.fetchStepContent(index);

      // Update step heading
      const count = this.getStepCount(index);
      if (this.els.stepHeading) {
        this.els.stepHeading.textContent = this.t('stepHeading', { step: index + 1, label: step.label, count, capacity: step.capacity });
      }

      // Update prev/next button states
      if (this.els.stepPrev) {
        this.els.stepPrev.disabled = index === 0;
      }
      if (this.els.stepNext) {
        this.els.stepNext.disabled = index === this.config.steps.length - 1;
      }

      // Clear filters when switching steps — filters are step-specific
      this.state.activeFilters = new Map();
      this.state.visibleCounts[index] = this.config.productsPerPage;
      this.updateFilterUI();

      // Apply pagination to active grid
      this.applyPagination(index);

      // Build filter options from the active grid's collection filters
      this.buildFilterOptions();

      // Refresh card states (qty caps, toggle enabled/disabled) for the now-active grid
      this.syncCardStates(index);

      // Scroll the step nav track so the active button is at the start
      this.scrollStepNavToActive(index);

      this.announce(this.t('viewingStep', { label: step.label }));
    }

    async fetchStepContent(stepIndex) {
      if (this.state.fetchedSteps.has(stepIndex)) return;

      const grid = this.element.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;

      const step = this.config.steps[stepIndex];
      const collectionHandle = step?.collectionHandle || grid.dataset.stepCollection;
      if (!collectionHandle) return;

      grid.classList.add('is-loading-step');

      try {
        const url = `${this.config.rootUrl}/collections/${collectionHandle}?section_id=${this.config.sectionId}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to load products');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract product cards from the fetched section
        const fetchedGrid = doc.querySelector('[data-ajax-step-grid]');
        if (fetchedGrid) {
          // Remove placeholder
          const placeholder = grid.querySelector('[data-step-placeholder]');
          if (placeholder) placeholder.remove();

          // Insert product cards before the Load More button
          const loadMoreBtn = grid.querySelector('[data-load-more]');
          fetchedGrid.querySelectorAll('.bundle-product-card').forEach(card => {
            grid.insertBefore(card, loadMoreBtn);
          });
        }

        this.state.fetchedSteps.add(stepIndex);

        // Restore selection states for any products already in the bundle
        this.restoreCardSelectionStates(stepIndex);

      } catch (error) {
        const placeholder = grid.querySelector('[data-step-placeholder]');
        if (placeholder) {
          placeholder.innerHTML = `<p class="bundle-builder__empty-msg">${this.escapeHtml(this.t('stepLoadError'))}</p>`;
        }
      } finally {
        grid.classList.remove('is-loading-step');
      }
    }

    restoreCardSelectionStates(stepIndex) {
      const grid = this.element.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;

      this.state.selectedProducts.forEach((selection) => {
        if (selection.stepIndex !== stepIndex) return;

        const card = grid.querySelector(`[data-product-id="${selection.productId}"]`);
        if (!card) return;

        // Check if the card's current variant matches this selection
        const currentVariantId = parseInt(card.dataset.variantId);
        if (currentVariantId === selection.variantId) {
          this.setCardSelectedState(card, true);
        }
      });
    }

    scrollStepNavToActive(index) {
      const btn = this.els.stepButtons[index];
      if (!btn) return;

      const track = btn.parentElement;
      if (!track || track.scrollWidth <= track.clientWidth) return;

      const scrollLeft = btn.offsetLeft - track.offsetLeft;
      track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }

    // ─── Product toggle (add/remove) ───────────────────────────

    handleToggle(card) {
      const productId = card.dataset.productId;
      const variantId = parseInt(card.dataset.variantId);
      const stepIndex = this.state.activeStep;

      // Check if this exact variant is already in bundle — toggle off
      const existingKey = this.findSelectionKeyByVariant(variantId, stepIndex);
      if (existingKey) {
        this.removeProduct(existingKey);
        return;
      }

      // Read requested quantity from the card's qty input
      const qtyInput = card.querySelector('[data-qty-input]');
      const requestedQty = Math.max(1, parseInt(qtyInput?.value) || 1);

      // Check step capacity
      const step = this.config.steps[stepIndex];
      const stepRemaining = step ? step.capacity - this.getStepCount(stepIndex) : Infinity;
      if (requestedQty > stepRemaining) {
        this.announce(this.t('stepFull', { label: step.label, capacity: step.capacity }));
        return;
      }

      // Check bundle max
      const bundleRemaining = this.config.bundleMax - this.getTotalQuantity();
      if (requestedQty > bundleRemaining) {
        this.announce(this.t('bundleFull', { max: this.config.bundleMax }));
        return;
      }

      // Check variant availability
      if (card.dataset.variantAvailable === 'false') {
        this.announce(this.t('variantSoldOut'));
        return;
      }

      // Deterministic key — one entry per variant per step
      const selectionKey = `${variantId}-${stepIndex}`;

      // Capture active colour swatch and size for variant label
      const activeRadio = card.querySelector('input[name^="swatch-"]:checked');
      const colourLabel = activeRadio ? activeRadio.value : '';
      const sizeDropdown = card.querySelector('[data-size-dropdown]');
      const sizeLabel = sizeDropdown ? sizeDropdown.options[sizeDropdown.selectedIndex]?.text.replace(/ - Sold out$/i, '') : '';
      const variantLabel = [colourLabel, sizeLabel].filter(Boolean).join(' / ');

      this.state.selectedProducts.set(selectionKey, {
        productId,
        variantId,
        title: card.dataset.productTitle,
        price: parseInt(card.dataset.productPrice) || 0,
        compareAtPrice: parseInt(card.dataset.productCompareAtPrice) || 0,
        image: card.querySelector('.bundle-product-card__image--primary')?.src || '',
        imageAlt: card.dataset.productTitle,
        variantLabel,
        stepIndex,
        quantity: requestedQty
      });

      // Update card visual state
      this.setCardSelectedState(card, true);

      // Reset qty input for when the card is deselected later
      if (qtyInput) qtyInput.value = 1;

      this.updateUI();
      this.announce(this.t('addedToBundleAnnounce', { title: card.dataset.productTitle }));

      // Auto-advance if step is now full
      if (step) {
        const newCount = this.getStepCount(stepIndex);
        if (newCount >= step.capacity) {
          this.autoAdvanceStep();
        }
      }
    }

    removeProduct(selectionKey) {
      const selection = this.state.selectedProducts.get(selectionKey);
      if (!selection) return;

      this.state.selectedProducts.delete(selectionKey);

      // Update card visual state — search within the correct step grid
      const stepGrid = this.element.querySelector(`[data-step-grid="${selection.stepIndex}"]`);
      const card = stepGrid ? stepGrid.querySelector(`[data-product-id="${selection.productId}"]`) : null;
      if (card) {
        // Check if the card's CURRENT variant is still selected
        const currentVariantId = parseInt(card.dataset.variantId);
        const stillSelected = this.findSelectionKeyByVariant(currentVariantId, selection.stepIndex);
        if (!stillSelected) {
          this.setCardSelectedState(card, false);
        }

        // Reset qty input to 1 and sync button states
        const qtyInput = card.querySelector('[data-qty-input]');
        if (qtyInput) {
          qtyInput.value = 1;
          const minus = card.querySelector('[data-qty-minus]');
          if (minus) minus.disabled = true;
        }
      }

      this.updateUI();
      this.announce(this.t('removedFromBundleAnnounce', { title: selection.title }));
    }

    // ─── Card state helpers ─────────────────────────────────

    setCardSelectedState(card, selected) {
      card.classList.toggle('is-selected', selected);
      const toggleAdd = card.querySelector('.bundle-product-card__toggle-add');
      const toggleRemove = card.querySelector('.bundle-product-card__toggle-remove');
      if (toggleAdd) toggleAdd.style.display = selected ? 'none' : '';
      if (toggleRemove) toggleRemove.style.display = selected ? '' : 'none';
    }

    // ─── Quantity helpers ─────────────────────────────────────

    adjustCardQty(card, delta) {
      const input = card.querySelector('[data-qty-input]');
      if (!input || card.classList.contains('is-selected')) return;

      const current = parseInt(input.value) || 1;
      const max = parseInt(input.max) || 1;
      const newVal = Math.max(1, Math.min(max, current + delta));
      input.value = newVal;

      const minus = card.querySelector('[data-qty-minus]');
      const plus = card.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = newVal <= 1;
      if (plus) plus.disabled = newVal >= max;
    }

    updateQtyInputs() {
      const stepIndex = this.state.activeStep;
      const remaining = this.getRemainingCapacity(stepIndex);
      const grid = this.element.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;

      grid.querySelectorAll('.bundle-product-card').forEach(card => {
        const input = card.querySelector('[data-qty-input]');
        if (!input) return;

        if (card.classList.contains('is-selected')) {
          // CSS hides the stepper; nothing to update
          return;
        }

        // Cap max to remaining capacity (at least 1 so the input is valid)
        const maxQty = Math.max(1, remaining);
        input.max = maxQty;
        if (parseInt(input.value) > maxQty) input.value = maxQty;

        const minus = card.querySelector('[data-qty-minus]');
        const plus = card.querySelector('[data-qty-plus]');
        if (minus) minus.disabled = parseInt(input.value) <= 1;
        if (plus) plus.disabled = parseInt(input.value) >= maxQty || remaining === 0;

        // Disable the entire toggle button if no capacity remains
        const toggle = card.querySelector('[data-bundle-toggle]');
        if (toggle) toggle.disabled = remaining === 0 || card.dataset.variantAvailable === 'false';
      });
    }

    /**
     * Re-sync every card in a step grid with the live selection state.
     * Needed when navigating back to a step after removals on another step.
     */
    syncCardStates(stepIndex) {
      const grid = this.element.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;

      grid.querySelectorAll('.bundle-product-card').forEach(card => {
        const currentVariantId = parseInt(card.dataset.variantId);
        const isSelected = !!this.findSelectionKeyByVariant(currentVariantId, stepIndex);

        this.setCardSelectedState(card, isSelected);

        // Reset qty to 1 for cards that are no longer selected
        if (!isSelected) {
          const qtyInput = card.querySelector('[data-qty-input]');
          if (qtyInput) {
            qtyInput.value = 1;
            const minus = card.querySelector('[data-qty-minus]');
            if (minus) minus.disabled = true;
          }
        }
      });

      // Now update qty input max values and toggle disabled states
      this.updateQtyInputs();
    }

    // ─── Variant handling ──────────────────────────────────────

    handleSizeChange(card, dropdown) {
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (!selectedOption) return;

      const newVariantId = parseInt(selectedOption.dataset.variantId);
      const isAvailable = !selectedOption.disabled;

      // Update card data attributes
      card.dataset.variantId = newVariantId;
      card.dataset.variantAvailable = isAvailable;

      // Check if this card has any selection at this step
      const anySelectionKey = this.findSelectionKey(card.dataset.productId, this.state.activeStep);
      if (anySelectionKey) {
        // Check if the NEW variant is already in the bundle
        const currentVariantKey = this.findSelectionKeyByVariant(newVariantId, this.state.activeStep);
        if (currentVariantKey) {
          // This exact variant is already selected — restore selected state and update data
          this.setCardSelectedState(card, true);
          const selection = this.state.selectedProducts.get(currentVariantKey);
          if (selection) {
            const variants = this.getVariantData(card);
            const newVariant = variants.find(v => v.id === newVariantId);
            if (newVariant) {
              selection.price = newVariant.price;
              selection.compareAtPrice = newVariant.compare_at_price || 0;
            }
            this.updateUI();
          }
        } else {
          // Product is selected but with a DIFFERENT variant — reset card to unselected
          this.setCardSelectedState(card, false);
        }
      }
    }

    handleSwatchClick(card, radioInput) {
      // Radio checked state is handled natively by the browser
      radioInput.checked = true;

      const colour = radioInput.dataset.colourValue;
      const variants = this.getVariantData(card);

      // Find initial variant matching this colour (used for image/price before size is resolved)
      let matchingVariant = variants.find(v => {
        return v.options && v.options.some(opt =>
          opt.toLowerCase() === colour.toLowerCase()
        );
      });

      if (matchingVariant) {

        // Update size dropdown first — this sets data-variant-id on each option
        const sizeDropdown = card.querySelector('[data-size-dropdown]');
        if (sizeDropdown) {
          this.updateSizeDropdownForColour(card, colour, variants);

          // Re-resolve variant using the currently selected size + new colour
          const selectedOption = sizeDropdown.options[sizeDropdown.selectedIndex];
          if (selectedOption?.dataset.variantId) {
            const sizeVariant = variants.find(v => v.id === parseInt(selectedOption.dataset.variantId));
            if (sizeVariant) matchingVariant = sizeVariant;
          }
        }

        card.dataset.variantId = matchingVariant.id;
        card.dataset.variantAvailable = matchingVariant.available;
        card.dataset.productPrice = matchingVariant.price;
        card.dataset.productCompareAtPrice = matchingVariant.compare_at_price || 0;

        // Update image if variant has featured media
        const featuredSrc = matchingVariant.featured_media?.preview_image?.src || matchingVariant.featured_media?.src;
        if (featuredSrc) {
          const primaryImg = card.querySelector('.bundle-product-card__image--primary');
          if (primaryImg) {
            primaryImg.src = this.sizedImageUrl(featuredSrc, 600);
            primaryImg.srcset = `${this.sizedImageUrl(featuredSrc, 400)} 400w, ${this.sizedImageUrl(featuredSrc, 600)} 600w`;
          }
        }

        // Update price display
        const priceEl = card.querySelector('.bundle-product-card__price');
        if (priceEl) {
          if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
            priceEl.innerHTML = `
              <span class="price price--highlight">${this.formatMoney(matchingVariant.price)}</span>
              <span class="price price--compare">${this.formatMoney(matchingVariant.compare_at_price)}</span>
            `;
          } else {
            priceEl.innerHTML = `<span class="price">${this.formatMoney(matchingVariant.price)}</span>`;
          }
        }

        // Check if this card has any selection at this step
        const anySelectionKey = this.findSelectionKey(card.dataset.productId, this.state.activeStep);
        if (anySelectionKey) {
          // Check if the resolved variant (colour + size) is already in the bundle
          const currentVariantKey = this.findSelectionKeyByVariant(matchingVariant.id, this.state.activeStep);
          if (currentVariantKey) {
            // This exact variant is already selected — restore selected state and update data
            this.setCardSelectedState(card, true);
            const selection = this.state.selectedProducts.get(currentVariantKey);
            if (selection) {
              selection.price = matchingVariant.price;
              selection.compareAtPrice = matchingVariant.compare_at_price || 0;
              const selFeaturedSrc = matchingVariant.featured_media?.preview_image?.src || matchingVariant.featured_media?.src;
              if (selFeaturedSrc) {
                selection.image = this.sizedImageUrl(selFeaturedSrc, 600);
              }
              this.updateUI();
            }
          } else {
            // Product is selected but with a DIFFERENT variant — reset card to unselected
            this.setCardSelectedState(card, false);
          }
        }
      }

      const toggle = card.querySelector('[data-bundle-toggle]')
        if (toggle) {
          toggle.disabled =
            !matchingVariant.available || card.dataset.variantAvailable === 'false'
        }
    }

    updateSizeDropdownForColour(card, colour, variants) {
      const sizeDropdown = card.querySelector('[data-size-dropdown]');
      if (!sizeDropdown) return;

      // Find all variants matching this colour and update size availability
      Array.from(sizeDropdown.options).forEach(option => {
        const sizeValue = option.value;
        const matchingVariant = variants.find(v =>
          v.options &&
          v.options.some(o => o.toLowerCase() === colour.toLowerCase()) &&
          v.options.some(o => o === sizeValue)
        );

        if (matchingVariant) {
          option.disabled = !matchingVariant.available;
          option.dataset.variantId = matchingVariant.id;
          option.textContent = matchingVariant.available ? sizeValue : `${sizeValue} - ${this.t('soldOut')}`;
        }
      });
    }

    // ─── Auto-advance step ─────────────────────────────────────

    autoAdvanceStep() {
      let nextIndex = null;
      for (let i = this.state.activeStep + 1; i < this.config.steps.length; i++) {
        if (this.getStepCount(i) < this.config.steps[i].capacity) {
          nextIndex = i;
          break;
        }
      }
      if (nextIndex === null) {
        for (let i = 0; i < this.state.activeStep; i++) {
          if (this.getStepCount(i) < this.config.steps[i].capacity) {
            nextIndex = i;
            break;
          }
        }
      }
      if (nextIndex === null) return;

      // Brief delay so the user sees their selection register before advancing
      setTimeout(() => {
        this.setActiveStep(nextIndex);
        this.scrollToTop();
      }, 350);
    }

    scrollToTop() {
      this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ─── Filter drawer ─────────────────────────────────────────

    /**
     * Get cached parsed data from a product card to avoid re-parsing JSON
     * on every filter change.
     */
    getCardData(card) {
      if (!card._bundleData) {
        card._bundleData = {
          options: (() => { try { return JSON.parse(card.dataset.productOptions || '[]'); } catch { return []; } })(),
          tags: (() => { try { return JSON.parse(card.dataset.productTags || '[]'); } catch { return []; } })(),
          price: parseInt(card.dataset.productPrice, 10) || 0
        };
      }
      return card._bundleData;
    }

    /**
     * Parse a Shopify filter param_name to determine the data source.
     * e.g. "filter.v.option.colour" → { type: 'variant_option', key: 'colour' }
     */
    getFilterSource(paramName) {
      if (paramName.startsWith('filter.v.option.')) {
        return { type: 'variant_option', key: paramName.replace('filter.v.option.', '') };
      }
      if (paramName === 'filter.p.tag') {
        return { type: 'tag' };
      }
      if (paramName === 'filter.v.price') {
        return { type: 'price' };
      }
      if (paramName.startsWith('filter.p.m.') || paramName.startsWith('filter.v.m.')) {
        return { type: 'metafield', key: paramName };
      }
      if (paramName === 'filter.p.product_type') {
        return { type: 'product_type' };
      }
      if (paramName === 'filter.p.vendor') {
        return { type: 'vendor' };
      }
      return { type: 'unknown' };
    }

    buildFilterOptions() {
      if (!this.els.filterSections) return;

      const stepFilters = this.config.steps[this.state.activeStep]?.filters;

      // If collection.filters data is available, use it; otherwise fall back to card scanning
      if (stepFilters && stepFilters.length > 0) {
        this.buildFilterOptionsFromConfig(stepFilters);
      } else {
        this.buildFilterOptionsFromCards();
      }
    }

    /**
     * Build filter sections from Shopify collection.filters config data.
     * This is the primary path — uses the store's configured storefront filters.
     */
    buildFilterOptionsFromConfig(filters) {
      const chevronSvg = this.getIconHtml('chevron');

      let html = '';

      filters.forEach(filter => {
        const paramName = filter.paramName;
        const activeSet = this.state.activeFilters.get(paramName);
        const hasActive = activeSet && (activeSet instanceof Set ? activeSet.size > 0 : true);
        const isOpen = hasActive; // start collapsed unless filter has active selections

        if (filter.type === 'list' && filter.values && filter.values.length > 0) {
          const filterLabelLower = filter.label.toLowerCase();
          const isColour = this.config.colorLabelList.includes(filterLabelLower);
          const isSize = this.config.sizeLabelList.includes(filterLabelLower);
          const safeParam = this.escapeHtml(paramName);

          // Build active-value summary and count for accordion header
          const activeCount = activeSet instanceof Set ? activeSet.size : 0;
          let summaryText = '';
          if (activeCount > 0) {
            summaryText = filter.values
              .filter(fv => activeSet.has(fv.value))
              .map(fv => this.escapeHtml(fv.label))
              .join(', ');
          }

          html += `<div class="bundle-filter-drawer__section">`;
          html += `<button type="button" class="bundle-filter-drawer__toggle" data-filter-toggle-section="${safeParam}" aria-expanded="${isOpen ? 'true' : 'false'}">`;
          html += `<span>${this.escapeHtml(filter.label)}</span>`;
          html += `<span class="collapsible-toggle__selected-value text--subdued text--small" data-filter-summary="${safeParam}">${summaryText}</span>`;
          html += chevronSvg;
          html += `</button>`;
          html += `<div class="bundle-filter-drawer__section-content" aria-hidden="${isOpen ? 'false' : 'true'}" data-filter-param="${safeParam}">`;

          if (isColour) {
            // Colour swatches — circles only, no text, matching theme's .color-swatch-list
            html += `<div class="color-swatch-list">`;
            filter.values.forEach((fv, idx) => {
              const isActive = activeSet instanceof Set && activeSet.has(fv.value);
              const isWhite = fv.label.toLowerCase() === 'white';
              const isDisabled = fv.count === 0;
              const safeValue = this.escapeHtml(fv.value);
              const safeLabel = this.escapeHtml(fv.label);
              const inputId = `bundle-filter-${safeParam}-${idx}`;

              html += `<div class="color-swatch${isWhite ? ' color-swatch--white' : ''}${isDisabled ? ' is-disabled' : ''}" data-tooltip="${safeLabel}">`;
              html += `<input class="color-swatch__radio visually-hidden" type="checkbox" id="${inputId}" data-filter-value="${safeValue}" data-filter-param="${safeParam}" data-filter-label="${safeLabel}" title="${safeLabel}"${isActive ? ' checked' : ''}${isDisabled ? ' disabled' : ''}>`;
              html += `<label class="color-swatch__item" for="${inputId}" style="${this.getSwatchStyle(fv.label)}"><span class="visually-hidden">${safeLabel}</span></label>`;
              html += `</div>`;
            });
            html += `</div>`;
          } else if (isSize) {
            // Size pills — block swatch tags, matching theme's .block-swatch-list--small
            html += `<div class="block-swatch-list block-swatch-list--small">`;
            filter.values.forEach((fv, idx) => {
              const isActive = activeSet instanceof Set && activeSet.has(fv.value);
              const isDisabled = fv.count === 0;
              const safeValue = this.escapeHtml(fv.value);
              const safeLabel = this.escapeHtml(fv.label);
              const inputId = `bundle-filter-${safeParam}-${idx}`;
              const countText = fv.count !== undefined ? ` (${fv.count})` : '';

              html += `<div class="block-swatch${isDisabled ? ' is-disabled' : ''}">`;
              html += `<input class="block-swatch__radio visually-hidden" type="checkbox" id="${inputId}" data-filter-value="${safeValue}" data-filter-param="${safeParam}" data-filter-label="${safeLabel}"${isActive ? ' checked' : ''}${isDisabled ? ' disabled' : ''}>`;
              html += `<label class="block-swatch__item" for="${inputId}">${safeLabel}${countText}</label>`;
              html += `</div>`;
            });
            html += `</div>`;
          } else {
            // Standard list — checkboxes with labels
            filter.values.forEach((fv, idx) => {
              const isActive = activeSet instanceof Set && activeSet.has(fv.value);
              const safeValue = this.escapeHtml(fv.value);
              const safeLabel = this.escapeHtml(fv.label);
              const inputId = `bundle-filter-${safeParam}-${idx}`;
              const countText = fv.count !== undefined ? ` (${fv.count})` : '';

              html += `<div class="checkbox-container">`;
              html += `<input class="checkbox" type="checkbox" id="${inputId}" data-filter-value="${safeValue}" data-filter-param="${safeParam}" data-filter-label="${safeLabel}"${isActive ? ' checked' : ''}>`;
              html += `<label for="${inputId}">${safeLabel}${countText}</label>`;
              html += `</div>`;
            });
          }

          html += `</div></div>`;

        } else if (filter.type === 'price_range') {
          const safeParam = this.escapeHtml(paramName);
          const activePrice = this.state.activeFilters.get(paramName);
          const rangeMin = (filter.rangeMin || 0) / 100;
          const rangeMax = (filter.rangeMax || 0) / 100;
          const currentMin = activePrice ? activePrice.min / 100 : rangeMin;
          const currentMax = activePrice ? (activePrice.max === Infinity ? rangeMax : activePrice.max / 100) : rangeMax;
          const currency = this.config.currencySymbol || '£';

          // Price range summary for accordion header
          let priceSummaryText = '';
          if (hasActive && activePrice) {
            const min = activePrice.min ? (activePrice.min / 100).toFixed(0) : '0';
            const max = activePrice.max === Infinity ? '+' : (activePrice.max / 100).toFixed(0);
            priceSummaryText = `${currency}${min} – ${currency}${max}`;
          }

          const lowerProgress = rangeMax > rangeMin ? (((currentMin - rangeMin) / (rangeMax - rangeMin)) * 100).toFixed(1) : '0';
          const upperProgress = rangeMax > rangeMin ? (((currentMax - rangeMin) / (rangeMax - rangeMin)) * 100).toFixed(1) : '100';

          html += `<div class="bundle-filter-drawer__section">`;
          html += `<button type="button" class="bundle-filter-drawer__toggle" data-filter-toggle-section="${safeParam}" aria-expanded="${hasActive ? 'true' : 'false'}">`;
          html += `<span>${this.escapeHtml(filter.label)}</span>`;
          html += `<span class="collapsible-toggle__selected-value text--subdued text--small" data-filter-summary="${safeParam}">${priceSummaryText}</span>`;
          html += chevronSvg;
          html += `</button>`;
          html += `<div class="bundle-filter-drawer__section-content" aria-hidden="${hasActive ? 'false' : 'true'}" data-filter-param="${safeParam}">`;
          html += `<div class="price-range">`;
          html += `<div class="price-range__range-group range-group" style="--range-min: ${lowerProgress}%; --range-max: ${upperProgress}%">`;
          html += `<input type="range" aria-label="${this.t('filterMinPrice')}" class="range" min="${rangeMin}" max="${rangeMax}" value="${currentMin}" data-filter-param="${safeParam}" data-price-range-min>`;
          html += `<input type="range" aria-label="${this.t('filterMaxPrice')}" class="range" min="${rangeMin}" max="${rangeMax}" value="${currentMax}" data-filter-param="${safeParam}" data-price-range-max>`;
          html += `</div>`;
          html += `<div class="price-range__input-group">`;
          html += `<div class="price-range__input input-prefix text--xsmall">`;
          html += `<span class="input-prefix__value text--subdued">${currency}</span>`;
          html += `<input aria-label="${this.t('filterMinPrice')}" class="input-prefix__field" type="number" inputmode="numeric" min="0" max="${currentMax}" placeholder="${rangeMin}" value="${activePrice ? currentMin : ''}" data-filter-param="${safeParam}" data-price-min>`;
          html += `</div>`;
          html += `<span class="price-range__delimiter text--small">${this.escapeHtml(this.t('filterPriceTo'))}</span>`;
          html += `<div class="price-range__input input-prefix text--xsmall">`;
          html += `<span class="input-prefix__value text--subdued">${currency}</span>`;
          html += `<input aria-label="${this.t('filterMaxPrice')}" class="input-prefix__field" type="number" inputmode="numeric" min="${currentMin}" max="${rangeMax}" placeholder="${rangeMax}" value="${activePrice ? currentMax : ''}" data-filter-param="${safeParam}" data-price-max>`;
          html += `</div>`;
          html += `</div>`;
          html += `</div>`;
          html += `</div></div>`;
        }
      });

      if (!html) {
        this.els.filterSections.innerHTML = `<p class="text--small text--subdued" style="padding: 20px 0;">${this.escapeHtml(this.t('filterNoFilters'))}</p>`;
        return;
      }

      this.els.filterSections.innerHTML = html;
      this.bindFilterEvents();
      this.updateFilterUI();
    }

    /**
     * Fallback: build filters by scanning product card data attributes.
     * Used when collection.filters isn't available for a step.
     */
    buildFilterOptionsFromCards() {
      const activeGrid = this.getActiveGrid();
      if (!activeGrid) return;

      const cards = activeGrid.querySelectorAll('.bundle-product-card');
      const optionGroups = new Map(); // optionName → Map<value, count>

      cards.forEach(card => {
        const data = this.getCardData(card);
        data.options.forEach(opt => {
          if (!optionGroups.has(opt.name)) {
            optionGroups.set(opt.name, new Map());
          }
          const counts = optionGroups.get(opt.name);
          opt.values.forEach(val => {
            counts.set(val, (counts.get(val) || 0) + 1);
          });
        });
      });

      if (optionGroups.size === 0) {
        this.els.filterSections.innerHTML = `<p class="text--small text--subdued" style="padding: 20px 0;">${this.escapeHtml(this.t('filterNoFilters'))}</p>`;
        return;
      }

      // Build price range from card prices
      let priceMin = Infinity;
      let priceMax = 0;
      cards.forEach(card => {
        const price = parseInt(card.dataset.productPrice, 10);
        if (!isNaN(price)) {
          if (price < priceMin) priceMin = price;
          if (price > priceMax) priceMax = price;
        }
      });

      // Convert to the same format as collection.filters for consistent rendering
      const syntheticFilters = [];
      optionGroups.forEach((valueCounts, optionName) => {
        const nameLower = optionName.toLowerCase();
        const isColour = this.config.colorLabelList.includes(nameLower);
        syntheticFilters.push({
          label: optionName,
          paramName: `filter.v.option.${optionName.toLowerCase()}`,
          type: 'list',
          presentation: isColour ? 'swatch' : '',
          values: Array.from(valueCounts.entries()).map(([v, count]) => ({ label: v, value: v, count }))
        });
      });

      // Add price range filter if we have valid price data
      if (priceMin < Infinity && priceMax > 0 && priceMin < priceMax) {
        syntheticFilters.push({
          label: this.t('filterPrice'),
          paramName: 'filter.v.price',
          type: 'price_range',
          rangeMin: priceMin,
          rangeMax: priceMax
        });
      }

      this.buildFilterOptionsFromConfig(syntheticFilters);
    }

    /**
     * Bind event listeners for filter accordion toggles, option clicks, and price inputs.
     */
    bindFilterEvents() {
      if (!this.els.filterSections) return;

      // Accordion toggles
      this.els.filterSections.querySelectorAll('[data-filter-toggle-section]').forEach(btn => {
        btn.addEventListener('click', () => {
          const isExpanded = btn.getAttribute('aria-expanded') === 'true';
          const newExpanded = !isExpanded;
          btn.setAttribute('aria-expanded', String(newExpanded));
          const content = btn.nextElementSibling;
          if (content) content.setAttribute('aria-hidden', String(!newExpanded));
        });
      });

      // All filter inputs: standard checkboxes, colour swatch radios, block swatch radios
      this.els.filterSections.querySelectorAll(
        '.checkbox[data-filter-value], .color-swatch__radio[data-filter-value], .block-swatch__radio[data-filter-value]'
      ).forEach(cb => {
        cb.addEventListener('change', () => {
          const paramName = cb.dataset.filterParam;
          const value = cb.dataset.filterValue;

          if (!this.state.activeFilters.has(paramName)) {
            this.state.activeFilters.set(paramName, new Set());
          }
          const activeSet = this.state.activeFilters.get(paramName);

          if (cb.checked) {
            activeSet.add(value);
          } else {
            activeSet.delete(value);
            if (activeSet.size === 0) {
              this.state.activeFilters.delete(paramName);
            }
          }

          this.applyFilters();
          this.updateFilterUI();
        });
      });

      // Price range sliders — update the CSS custom properties for the active bar
      // and sync with the text inputs below them
      this.els.filterSections.querySelectorAll('[data-price-range-min], [data-price-range-max]').forEach(rangeInput => {
        rangeInput.addEventListener('input', () => {
          const rangeGroup = rangeInput.closest('.range-group');
          const section = rangeInput.closest('.bundle-filter-drawer__section-content');
          if (!rangeGroup || !section) return;

          const minRange = section.querySelector('[data-price-range-min]');
          const maxRange = section.querySelector('[data-price-range-max]');
          const minVal = parseFloat(minRange.value);
          const maxVal = parseFloat(maxRange.value);

          // Prevent min from exceeding max and vice versa
          if (rangeInput.hasAttribute('data-price-range-min') && minVal > maxVal) {
            rangeInput.value = maxVal;
          } else if (rangeInput.hasAttribute('data-price-range-max') && maxVal < minVal) {
            rangeInput.value = minVal;
          }

          // Update CSS custom properties for the active bar
          const rangeMinAttr = parseFloat(minRange.min);
          const rangeMaxAttr = parseFloat(minRange.max);
          const span = rangeMaxAttr - rangeMinAttr;
          if (span > 0) {
            rangeGroup.style.setProperty('--range-min', `${((parseFloat(minRange.value) - rangeMinAttr) / span) * 100}%`);
            rangeGroup.style.setProperty('--range-max', `${((parseFloat(maxRange.value) - rangeMinAttr) / span) * 100}%`);
          }

          // Sync text inputs
          const minText = section.querySelector('[data-price-min]');
          const maxText = section.querySelector('[data-price-max]');
          if (minText) minText.value = parseFloat(minRange.value).toFixed(0);
          if (maxText) maxText.value = parseFloat(maxRange.value).toFixed(0);

          // Update summary in accordion header
          const paramName = rangeInput.dataset.filterParam;
          const currency = this.config.currencySymbol || '£';
          const summaryEl = this.els.filterSections.querySelector(`[data-filter-summary="${paramName}"]`);
          if (summaryEl) {
            summaryEl.textContent = `${currency}${parseFloat(minRange.value).toFixed(0)} – ${currency}${parseFloat(maxRange.value).toFixed(0)}`;
          }
        });
      });

      // Price range text inputs — select on focus (replaces theme's <price-range> behaviour)
      this.els.filterSections.querySelectorAll('.input-prefix__field[data-price-min], .input-prefix__field[data-price-max]').forEach(input => {
        input.addEventListener('focus', () => input.select());
      });

      // Price range text inputs — apply filter on change and sync sliders
      let priceTimeout;
      this.els.filterSections.querySelectorAll('.input-prefix__field[data-price-min], .input-prefix__field[data-price-max]').forEach(input => {
        input.addEventListener('change', () => {
          clearTimeout(priceTimeout);
          priceTimeout = setTimeout(() => {
            const paramName = input.dataset.filterParam;
            const section = input.closest('.bundle-filter-drawer__section-content');
            if (!section) return;

            const minInput = section.querySelector('[data-price-min]');
            const maxInput = section.querySelector('[data-price-max]');

            // Clamp text inputs
            const rangeMinEl = section.querySelector('[data-price-range-min]');
            const rangeMaxEl = section.querySelector('[data-price-range-max]');
            if (minInput && rangeMinEl) {
              minInput.value = Math.max(Math.min(parseInt(minInput.value) || 0, parseInt(maxInput?.value || rangeMinEl.max) - 1), parseInt(rangeMinEl.min));
            }
            if (maxInput && rangeMaxEl) {
              maxInput.value = Math.min(Math.max(parseInt(maxInput.value) || 0, parseInt(minInput?.value || rangeMaxEl.min) + 1), parseInt(rangeMaxEl.max));
            }

            // Sync range sliders to text inputs
            if (rangeMinEl && minInput) rangeMinEl.value = minInput.value;
            if (rangeMaxEl && maxInput) rangeMaxEl.value = maxInput.value;

            // Update CSS custom properties for the active bar
            const rangeGroup = section.querySelector('.range-group');
            if (rangeGroup && rangeMinEl) {
              const rangeMinAttr = parseFloat(rangeMinEl.min);
              const rangeMaxAttr = parseFloat(rangeMinEl.max);
              const span = rangeMaxAttr - rangeMinAttr;
              if (span > 0) {
                rangeGroup.style.setProperty('--range-min', `${((parseFloat(rangeMinEl.value) - rangeMinAttr) / span) * 100}%`);
                rangeGroup.style.setProperty('--range-max', `${((parseFloat(rangeMaxEl.value) - rangeMinAttr) / span) * 100}%`);
              }
            }

            const minVal = minInput?.value ? Math.round(parseFloat(minInput.value) * 100) : null;
            const maxVal = maxInput?.value ? Math.round(parseFloat(maxInput.value) * 100) : null;

            if (minVal !== null || maxVal !== null) {
              this.state.activeFilters.set(paramName, {
                min: minVal || 0,
                max: maxVal || Infinity
              });
            } else {
              this.state.activeFilters.delete(paramName);
            }

            this.applyFilters();
            this.updateFilterUI();
          }, 300);
        });
      });

      // Price range sliders — apply filter on change (after drag ends)
      this.els.filterSections.querySelectorAll('[data-price-range-min], [data-price-range-max]').forEach(rangeInput => {
        rangeInput.addEventListener('change', () => {
          const paramName = rangeInput.dataset.filterParam;
          const section = rangeInput.closest('.bundle-filter-drawer__section-content');
          if (!section) return;

          const minRange = section.querySelector('[data-price-range-min]');
          const maxRange = section.querySelector('[data-price-range-max]');
          const minVal = Math.round(parseFloat(minRange.value) * 100);
          const maxVal = Math.round(parseFloat(maxRange.value) * 100);

          if (minVal > 0 || maxVal < Math.round(parseFloat(maxRange.max) * 100)) {
            this.state.activeFilters.set(paramName, { min: minVal, max: maxVal });
          } else {
            this.state.activeFilters.delete(paramName);
          }

          this.applyFilters();
          this.updateFilterUI();
        });
      });
    }

    openFilterDrawer() {
      if (!this.els.filterDrawer) return;
      this.filterTrigger = document.activeElement;
      this.els.filterDrawer.setAttribute('aria-hidden', 'false');
      this.els.filterDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Move focus to close button
      const closeBtn = this.els.filterDrawer.querySelector('[data-filter-close]');
      if (closeBtn) closeBtn.focus();

      // Trap focus + Escape
      this.filterKeyHandler = (e) => {
        if (e.key === 'Escape') {
          this.closeFilterDrawer();
          return;
        }
        if (e.key !== 'Tab') return;

        const focusable = [...this.els.filterDrawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )].filter(el => !el.disabled && el.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      this.els.filterDrawer.addEventListener('keydown', this.filterKeyHandler);
    }

    closeFilterDrawer() {
      if (!this.els.filterDrawer) return;
      this.els.filterDrawer.setAttribute('aria-hidden', 'true');
      this.els.filterDrawer.classList.remove('is-open');
      document.body.style.overflow = '';

      // Remove focus trap
      if (this.filterKeyHandler) {
        this.els.filterDrawer.removeEventListener('keydown', this.filterKeyHandler);
        this.filterKeyHandler = null;
      }

      // Return focus to trigger
      if (this.filterTrigger) {
        this.filterTrigger.focus();
        this.filterTrigger = null;
      }
    }

    /**
     * Apply all active filters to the current grid. A card is visible
     * only if it matches every active filter (AND across categories,
     * OR within a category for list filters).
     *
     * Sets data-filtered attribute on each card, then calls applyPagination
     * to handle display with Load More.
     */
    applyFilters() {
      const activeGrid = this.getActiveGrid();
      if (!activeGrid) return;

      const cards = activeGrid.querySelectorAll('.bundle-product-card');
      const hasFilters = this.state.activeFilters.size > 0;

      // Reset pagination to page 1 when filters change
      this.state.visibleCounts[this.state.activeStep] = this.config.productsPerPage;

      let matchCount = 0;

      cards.forEach(card => {
        if (!hasFilters) {
          card.dataset.filtered = 'true';
          matchCount++;
          return;
        }

        const data = this.getCardData(card);
        let visible = true;

        for (const [paramName, filterValue] of this.state.activeFilters) {
          const source = this.getFilterSource(paramName);

          if (source.type === 'variant_option') {
            // filterValue is a Set of selected option values
            const option = data.options.find(o => o.name.toLowerCase() === source.key.toLowerCase());
            if (!option) {
              // Product doesn't have this option — hide it
              visible = false;
              break;
            }
            // Check if any of the product's values for this option intersect the selected values
            const hasMatch = option.values.some(v => filterValue.has(v));
            if (!hasMatch) {
              visible = false;
              break;
            }

          } else if (source.type === 'tag') {
            // filterValue is a Set of selected tag strings
            const hasMatch = data.tags.some(tag => filterValue.has(tag));
            if (!hasMatch) {
              visible = false;
              break;
            }

          } else if (source.type === 'price') {
            // filterValue is { min, max } in cents
            const price = data.price;
            if (price < filterValue.min || price > filterValue.max) {
              visible = false;
              break;
            }

          } else {
            // Unknown filter type — skip (don't hide for filters we can't match)
          }
        }

        card.dataset.filtered = visible ? 'true' : 'false';
        if (visible) matchCount++;
      });

      // Show/hide no-results message
      this.toggleNoResultsMessage(activeGrid, matchCount === 0 && hasFilters);

      // Re-apply pagination with the new filtered state
      this.applyPagination(this.state.activeStep);

      // Sync card images to match the active colour filter
      this.syncCardsToActiveColourFilter();
    }

    /**
     * When exactly one colour is selected in the filter, switch all visible
     * cards to show that colour variant's image and price.
     */
    syncCardsToActiveColourFilter() {
      let activeColour = null;

      for (const [paramName, filterValue] of this.state.activeFilters) {
        const source = this.getFilterSource(paramName);
        if (source.type === 'variant_option') {
          const isColour = this.config.colorLabelList.includes(source.key.toLowerCase());
          if (isColour && filterValue instanceof Set && filterValue.size === 1) {
            activeColour = [...filterValue][0];
          }
        }
      }

      if (!activeColour) return;

      const activeGrid = this.getActiveGrid();
      if (!activeGrid) return;

      activeGrid.querySelectorAll('.bundle-product-card[data-filtered="true"]').forEach(card => {
        const radio = card.querySelector(`input[data-colour-value="${CSS.escape(activeColour)}"]`);
        if (radio && !radio.checked) {
          this.handleSwatchClick(card, radio);
        }
      });
    }

    /**
     * Show or hide a "no products match" message within the grid.
     */
    toggleNoResultsMessage(grid, show) {
      let msg = grid.querySelector('.bundle-builder__no-results');
      if (show) {
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'bundle-builder__no-results';
          msg.textContent = this.t('filterNoResults');
          grid.appendChild(msg);
        }
        msg.style.display = '';
      } else if (msg) {
        msg.style.display = 'none';
      }
    }

    /**
     * Remove a single filter value, or clear an entire filter category.
     * @param {string} paramName - The filter param name
     * @param {string} [value] - If provided, removes just this value; otherwise clears the whole category
     */
    clearFilter(paramName, value) {
      if (value !== undefined) {
        const activeSet = this.state.activeFilters.get(paramName);
        if (activeSet instanceof Set) {
          activeSet.delete(value);
          if (activeSet.size === 0) {
            this.state.activeFilters.delete(paramName);
          }
        } else {
          this.state.activeFilters.delete(paramName);
        }
      } else if (paramName) {
        this.state.activeFilters.delete(paramName);
      } else {
        this.state.activeFilters = new Map();
      }
      this.applyFilters();
      this.updateFilterUI();
      this.buildFilterOptions(); // refresh drawer active states
    }

    clearAllFilters() {
      this.state.activeFilters = new Map();
      this.applyFilters();
      this.updateFilterUI();
      this.buildFilterOptions();
    }

    /**
     * Update filter UI elements: drawer badge, clear-all visibility, toolbar button badge.
     */
    updateFilterUI() {
      let count = 0;
      this.state.activeFilters.forEach(filterValue => {
        if (filterValue instanceof Set) {
          count += filterValue.size;
        } else if (filterValue.min !== undefined || filterValue.max !== undefined) {
          count += 1;
        }
      });

      // Drawer header badge
      if (this.els.filterBadge) {
        this.els.filterBadge.textContent = count;
        this.els.filterBadge.style.display = count > 0 ? '' : 'none';
      }

      // Drawer "Clear all" link
      if (this.els.filterClearAll) {
        this.els.filterClearAll.style.display = count > 0 ? '' : 'none';
      }

      // Toolbar filter button badge
      if (this.els.filterBtnBadge) {
        this.els.filterBtnBadge.textContent = count;
        this.els.filterBtnBadge.style.display = count > 0 ? '' : 'none';
      }

      // Update per-section accordion header summaries and badges
      if (this.els.filterSections) {
        this.els.filterSections.querySelectorAll('[data-filter-summary]').forEach(summaryEl => {
          const paramName = summaryEl.dataset.filterSummary;
          const activeSet = this.state.activeFilters.get(paramName);

          if (activeSet instanceof Set && activeSet.size > 0) {
            // Gather labels from checked inputs in this section
            const section = this.els.filterSections.querySelector(`[data-filter-param="${paramName}"]`);
            const labels = [];
            if (section) {
              section.querySelectorAll('input[data-filter-label]:checked').forEach(input => {
                labels.push(input.dataset.filterLabel);
              });
            }
            summaryEl.textContent = labels.join(', ');
          } else if (activeSet && !(activeSet instanceof Set)) {
            // Price range — update summary
            const currency = this.config.currencySymbol || '£';
            const min = activeSet.min ? (activeSet.min / 100).toFixed(0) : '0';
            const max = activeSet.max === Infinity ? '+' : (activeSet.max / 100).toFixed(0);
            summaryEl.textContent = `${currency}${min} – ${currency}${max}`;
          } else {
            summaryEl.textContent = '';
          }
        });

      }
    }

    /**
     * Get the currently active step grid element.
     */
    getActiveGrid() {
      return this.element.querySelector(`[data-step-grid="${this.state.activeStep}"]`);
    }

    // ─── Quick-view modal ──────────────────────────────────────

    /**
     * Open the centred quick-view modal for a product.
     * Fetches full product data via Shopify AJAX API.
     */
    async openQuickView(productUrl) {
      if (!this.els.quickView) {
        window.open(productUrl, '_blank');
        return;
      }

      // Extract handle from URL (e.g. /products/maisie-tbar-necklace)
      const handle = productUrl.replace(/.*\/products\//, '').split('?')[0];
      if (!handle) return;

      // Store reference to the originating card for the "Add to Bundle" action
      const card = this.element.querySelector(`[data-product-handle="${handle}"]`);
      this._quickViewCard = card;

      // Show modal immediately with loading state
      this.els.quickView.setAttribute('aria-hidden', 'false');
      this.els.quickView.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Reset content
      if (this.els.quickViewTitle) this.els.quickViewTitle.textContent = this.t('quickViewLoading');
      if (this.els.quickViewPrice) this.els.quickViewPrice.innerHTML = '';
      if (this.els.quickViewSwatches) this.els.quickViewSwatches.innerHTML = '';
      if (this.els.quickViewDescription) this.els.quickViewDescription.innerHTML = '';
      if (this.els.quickViewThumbs) this.els.quickViewThumbs.innerHTML = '';
      if (this.els.quickViewAdd) this.els.quickViewAdd.disabled = true;

      try {
        const response = await fetch(`${this.config.rootUrl}/products/${handle}.js`);
        if (!response.ok) throw new Error(this.t('quickViewError'));
        const product = await response.json();

        this.renderQuickView(product, card);
      } catch {
        if (this.els.quickViewTitle) this.els.quickViewTitle.textContent = this.t('quickViewError');
      }
    }

    /**
     * Populate the quick-view modal with product data.
     */
    renderQuickView(product, card) {
      // Title
      if (this.els.quickViewTitle) {
        this.els.quickViewTitle.textContent = product.title;
      }

      // Price
      if (this.els.quickViewPrice) {
        const variant = product.variants[0];
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.els.quickViewPrice.innerHTML = `
            <span class="price price--highlight">${this.formatMoney(variant.price)}</span>
            <span class="price price--compare">${this.formatMoney(variant.compare_at_price)}</span>
          `;
        } else {
          this.els.quickViewPrice.innerHTML = `<span class="price">${this.formatMoney(variant.price)}</span>`;
        }
      }

      // Build a normalised images array — .js may return media[] or just images[]
      const mediaItems = [];
      if (product.media && product.media.length > 0) {
        product.media.forEach(m => {
          if (!m.media_type || m.media_type === 'image') {
            mediaItems.push({ src: m.src, alt: m.alt || product.title });
          }
        });
      } else if (product.images && product.images.length > 0) {
        product.images.forEach(url => {
          mediaItems.push({ src: url, alt: product.title });
        });
      }

      // Featured image — request sized version from Shopify CDN
      if (this.els.quickViewImage && mediaItems.length > 0) {
        const imgSrc = mediaItems[0].src;
        this.els.quickViewImage.src = this.sizedImageUrl(imgSrc, 800);
        this.els.quickViewImage.srcset = `${this.sizedImageUrl(imgSrc, 400)} 400w, ${this.sizedImageUrl(imgSrc, 600)} 600w, ${this.sizedImageUrl(imgSrc, 800)} 800w`;
        this.els.quickViewImage.alt = mediaItems[0].alt;
      }

      // Thumbnails
      if (this.els.quickViewThumbs && mediaItems.length > 1) {
        let thumbsHtml = '';
        mediaItems.forEach((img, i) => {
          // Modern Shopify CDN uses ?width= params for resizing
          const thumbSrc = img.src.includes('?')
            ? img.src + '&width=120'
            : img.src + '?width=120';
          thumbsHtml += `<button type="button" class="bundle-quick-view__thumb${i === 0 ? ' is-active' : ''}" data-quick-view-thumb="${i}" data-full-src="${this.escapeHtml(img.src)}">
            <img src="${thumbSrc}" alt="${this.escapeHtml(img.alt)}" loading="lazy">
          </button>`;
        });
        this.els.quickViewThumbs.innerHTML = thumbsHtml;

        // Bind thumbnail clicks
        this.els.quickViewThumbs.querySelectorAll('[data-quick-view-thumb]').forEach(btn => {
          btn.addEventListener('click', () => {
            if (this.els.quickViewImage) {
              const fullSrc = btn.dataset.fullSrc;
              this.els.quickViewImage.src = this.sizedImageUrl(fullSrc, 800);
              this.els.quickViewImage.srcset = `${this.sizedImageUrl(fullSrc, 400)} 400w, ${this.sizedImageUrl(fullSrc, 600)} 600w, ${this.sizedImageUrl(fullSrc, 800)} 800w`;
            }
            this.els.quickViewThumbs.querySelectorAll('[data-quick-view-thumb]').forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');
          });
        });
      }

      // Colour swatches (if the product has a colour option)
      // product.options from .js API is [{name, position, values}, ...]
      if (this.els.quickViewSwatches) {
        const colourOpt = product.options.find(opt =>
          this.config.colorLabelList.includes(opt.name.toLowerCase())
        );
        if (colourOpt) {
          const optIndex = colourOpt.position - 1; // position is 1-based
          let swatchesHtml = '';
          colourOpt.values.forEach((val, i) => {
            const handle = val.toLowerCase().replace(/\s+/g, '-');
            swatchesHtml += `<button type="button" class="bundle-product-card__swatch${i === 0 ? ' is-active' : ''}" data-quick-view-colour="${this.escapeHtml(val)}" style="${this.getSwatchStyle(val)}" aria-label="${this.escapeHtml(val)}"></button>`;
          });
          this.els.quickViewSwatches.innerHTML = swatchesHtml;

          // Bind swatch clicks to update image
          this.els.quickViewSwatches.querySelectorAll('[data-quick-view-colour]').forEach(btn => {
            btn.addEventListener('click', () => {
              this.els.quickViewSwatches.querySelectorAll('[data-quick-view-colour]').forEach(b => b.classList.remove('is-active'));
              btn.classList.add('is-active');

              // Find variant matching this colour and update image
              const colour = btn.dataset.quickViewColour;
              const matchingVariant = product.variants.find(v => v.options[optIndex] === colour);
              if (matchingVariant && matchingVariant.featured_image) {
                const imgSrc = matchingVariant.featured_image.src || matchingVariant.featured_image;
                if (this.els.quickViewImage) {
                  this.els.quickViewImage.src = this.sizedImageUrl(imgSrc, 800);
                  this.els.quickViewImage.srcset = `${this.sizedImageUrl(imgSrc, 400)} 400w, ${this.sizedImageUrl(imgSrc, 600)} 600w, ${this.sizedImageUrl(imgSrc, 800)} 800w`;
                }
              }
            });
          });
        }
      }

      // Description — .js API uses "description", not "body_html"
      // Sanitise via DOMParser to strip any scripts from store-managed HTML
      if (product.description && this.els.quickViewDescription) {
        const parsed = new DOMParser().parseFromString(product.description, 'text/html');
        parsed.querySelectorAll('script,iframe,object,embed').forEach(el => el.remove());
        this.els.quickViewDescription.innerHTML = parsed.body.innerHTML;
      }

      // Enable CTA if card is available and not already at step capacity
      if (this.els.quickViewAdd) {
        const isSelected = card && card.classList.contains('is-selected');
        const isAvailable = card && card.dataset.variantAvailable !== 'false';
        this.els.quickViewAdd.disabled = !isAvailable || isSelected;
        this.els.quickViewAdd.textContent = isSelected ? this.t('quickViewAlready') : this.t('quickViewAdd');
      }
    }

    closeQuickView() {
      if (!this.els.quickView) return;
      this.els.quickView.setAttribute('aria-hidden', 'true');
      this.els.quickView.classList.remove('is-open');
      document.body.style.overflow = '';
      this._quickViewCard = null;
    }

    /**
     * Add the current quick-view product to the bundle, then close.
     */
    addFromQuickView() {
      const card = this._quickViewCard;
      if (card) {
        this.handleToggle(card);
        this.closeQuickView();
      }
    }

    // ─── Pagination (Load More) ───────────────────────────────

    applyPagination(stepIndex) {
      const grid = this.element.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;

      const cards = grid.querySelectorAll('.bundle-product-card');
      const perPage = this.config.productsPerPage;
      const hasFilters = this.state.activeFilters.size > 0;

      // Initialise visible count if not set
      if (!(stepIndex in this.state.visibleCounts)) {
        this.state.visibleCounts[stepIndex] = perPage;
      }

      const limit = this.state.visibleCounts[stepIndex];
      let visibleSoFar = 0;
      let totalMatching = 0;

      cards.forEach(card => {
        // Check filter state first
        const matchesFilter = !hasFilters || card.dataset.filtered !== 'false';

        if (!matchesFilter) {
          card.style.display = 'none';
          return;
        }

        totalMatching++;

        if (visibleSoFar < limit) {
          card.style.display = '';
          visibleSoFar++;
        } else {
          card.style.display = 'none';
        }
      });

      // Show/hide Load More button
      const loadMoreBtn = this.element.querySelector(`[data-load-more="${stepIndex}"]`);
      if (loadMoreBtn) {
        loadMoreBtn.style.display = visibleSoFar < totalMatching ? '' : 'none';
      }
    }

    loadMore(stepIndex) {
      const perPage = this.config.productsPerPage;
      const prevCount = this.state.visibleCounts[stepIndex] || perPage;
      this.state.visibleCounts[stepIndex] = prevCount + perPage;
      this.applyPagination(stepIndex);

      // Focus the first newly visible card
      const activeGrid = this.getActiveGrid();
      if (activeGrid) {
        const visibleCards = [...activeGrid.querySelectorAll('.bundle-product-card')].filter(c => c.style.display !== 'none');
        const firstNew = visibleCards[prevCount];
        if (firstNew) {
          const focusTarget = firstNew.querySelector('a, button, input, select') || firstNew;
          focusTarget.focus({ preventScroll: true });
        }
      }
    }

    // ─── Bottom sheet (mobile) ─────────────────────────────────

    toggleBottomSheet() {
      if (!this.els.bottomSheetContent) return;
      const isExpanded = this.els.bottomSheetContent.getAttribute('aria-hidden') === 'false';

      this.els.bottomSheetContent.setAttribute('aria-hidden', isExpanded);
      this.els.bottomSheet.classList.toggle('is-expanded', !isExpanded);
      if (this.els.bottomSheetToggle) {
        this.els.bottomSheetToggle.setAttribute('aria-expanded', !isExpanded);
        this.els.bottomSheetToggle.setAttribute('aria-label',
          isExpanded
            ? this.t('expandSummary') || 'Expand bundle summary'
            : this.t('collapseSummary') || 'Collapse bundle summary'
        );
      }
    }

    // ─── UI updates ────────────────────────────────────────────

    updateUI() {
      this.updateStepCounts();
      this.updateStepHeading();
      this.updateSidebar();
      this.updateMobileSheet();
      this.updateCTAState();
      this.updateQtyInputs();
    }

    updateStepHeading() {
      const step = this.config.steps[this.state.activeStep];
      if (!step || !this.els.stepHeading) return;
      const count = this.getStepCount(this.state.activeStep);
      this.els.stepHeading.textContent = this.t('stepHeading', { step: this.state.activeStep + 1, label: step.label, count, capacity: step.capacity });
    }

    updateStepCounts() {
      this.config.steps.forEach((step, index) => {
        const count = this.getStepCount(index);
        const countEl = this.element.querySelector(`[data-step-count="${index}"]`);
        if (countEl) countEl.textContent = count;

        // Update badge
        const badgeEl = this.element.querySelector(`[data-step-badge="${index}"]`);
        if (badgeEl) {
          badgeEl.textContent = count;
          badgeEl.classList.toggle('has-count', count > 0);
        }

        // Update button state
        const btn = this.els.stepButtons[index];
        if (btn) {
          btn.classList.toggle('is-complete', count >= step.capacity);
        }
      });
    }

    updateSidebar() {
      const totalCount = this.getTotalQuantity();
      const totals = this.calculateTotals();

      // Count + progress bar
      if (this.els.sidebarCount) {
        this.els.sidebarCount.textContent = totalCount;
      }
      if (this.els.progressFill) {
        const pct = Math.min(100, Math.round((totalCount / this.config.bundleMax) * 100));
        this.els.progressFill.style.width = pct + '%';
      }
      if (this.els.progressTrack) {
        this.els.progressTrack.setAttribute('aria-valuenow', totalCount);
      }

      // Items list
      if (this.els.sidebarItems) {
        if (totalCount === 0) {
          this.els.sidebarItems.innerHTML = `<p class="bundle-sidebar__empty-msg">${this.escapeHtml(this.t('sidebarEmpty'))}</p>`;
        } else {
          let html = '<ul class="bundle-sidebar__list">';
          this.state.selectedProducts.forEach((sel, key) => {
            const safeTitle = this.escapeHtml(sel.title);
            const safeVariant = sel.variantLabel ? this.escapeHtml(sel.variantLabel) : '';
            const qtyPrefix = (sel.quantity || 1) > 1 ? `${sel.quantity} &times; ` : '';
            html += `
              <li class="bundle-sidebar__item">
                <div class="bundle-sidebar__item-info">
                  <span class="bundle-sidebar__item-title">${qtyPrefix}${safeTitle}</span>
                  ${safeVariant ? `<span class="bundle-sidebar__item-variant">${safeVariant}</span>` : ''}
                </div>
                <button type="button" class="bundle-sidebar__item-remove" data-remove-selection="${key}" aria-label="${this.t('removeItem', { title: safeTitle })}">
                  ${this.t('remove')} ${this.getIconHtml('trash')}
                </button>
              </li>
            `;
          });
          html += '</ul>';
          this.els.sidebarItems.innerHTML = html;
        }
        this.updateSidebarScrollGradients();
      }

      // Pricing
      if (this.els.sidebarPricing) {
        this.els.sidebarPricing.style.display = totalCount > 0 ? '' : 'none';
      }
      if (this.els.sidebarSubtotal) {
        this.els.sidebarSubtotal.textContent = this.formatMoney(totals.subtotal);
      }
      if (this.els.sidebarDiscountRow) {
        this.els.sidebarDiscountRow.style.display = totals.qualifies ? '' : 'none';
      }
      if (this.els.sidebarDiscountAmount) {
        this.els.sidebarDiscountAmount.textContent = `-${this.formatMoney(totals.discount)}`;
      }
      if (this.els.sidebarTotal) {
        this.els.sidebarTotal.textContent = this.formatMoney(totals.total);
      }

      // Discount message
      if (this.els.discountMsg) {
        this.els.discountMsg.style.display = totals.qualifies ? '' : 'none';
      }

      // Savings message
      if (this.els.savingsMsg) {
        if (totals.qualifies && totals.discount > 0) {
          this.els.savingsMsg.textContent = this.t('savedMessage', { amount: this.formatMoney(totals.discount) });
          this.els.savingsMsg.style.display = '';
        } else {
          this.els.savingsMsg.style.display = 'none';
        }
      }
    }

    updateSidebarScrollGradients() {
      const wrapper = this.els.sidebarItemsWrapper;
      const list = this.els.sidebarItems;
      if (!wrapper || !list) return;

      const update = () => {
        const canScrollUp = list.scrollTop > 2;
        const canScrollDown = list.scrollHeight - list.scrollTop - list.clientHeight > 2;
        wrapper.classList.toggle('can-scroll-up', canScrollUp);
        wrapper.classList.toggle('can-scroll-down', canScrollDown);
      };

      // Run once now, and attach scroll listener if not already done
      update();
      if (!list._scrollBound) {
        list.addEventListener('scroll', update, { passive: true });
        list._scrollBound = true;
      }
    }

    updateMobileSheet() {
      const totalCount = this.getTotalQuantity();
      const totals = this.calculateTotals();

      // Show/hide bottom sheet
      if (this.els.bottomSheet) {
        this.els.bottomSheet.classList.toggle('is-visible', totalCount > 0);
      }

      if (this.els.mobileSheetCount) {
        this.els.mobileSheetCount.textContent = `${totalCount}/${this.config.bundleMax}`;
      }
      if (this.els.mobileSheetSubtotal) {
        this.els.mobileSheetSubtotal.textContent = this.formatMoney(totals.total);
      }

      // Mobile items list
      if (this.els.mobileSheetItems) {
        if (totalCount === 0) {
          this.els.mobileSheetItems.innerHTML = '';
        } else {
          let html = '';
          this.state.selectedProducts.forEach((sel, key) => {
            const safeTitle = this.escapeHtml(sel.title);
            const safeVariant = sel.variantLabel ? this.escapeHtml(sel.variantLabel) : '';
            const qtyPrefix = (sel.quantity || 1) > 1 ? `${sel.quantity} &times; ` : '';
            html += `
              <div class="bundle-bottom-sheet__item">
                <div class="bundle-bottom-sheet__item-info">
                  <span>${qtyPrefix}${safeTitle}</span>
                  ${safeVariant ? `<span class="bundle-bottom-sheet__item-variant">${safeVariant}</span>` : ''}
                </div>
                <button type="button" class="bundle-bottom-sheet__item-remove" data-remove-selection="${key}" aria-label="${this.t('removeItem', { title: safeTitle })}">${this.t('remove')}</button>
              </div>
            `;
          });
          this.els.mobileSheetItems.innerHTML = html;
        }
      }

      // Mobile pricing
      if (this.els.mobileSheetPricing) {
        this.els.mobileSheetPricing.style.display = totalCount > 0 ? '' : 'none';
      }
      if (this.els.mobileSubtotal) {
        this.els.mobileSubtotal.textContent = this.formatMoney(totals.subtotal);
      }
      if (this.els.mobileDiscountRow) {
        this.els.mobileDiscountRow.style.display = totals.qualifies ? '' : 'none';
      }
      if (this.els.mobileDiscountAmount) {
        this.els.mobileDiscountAmount.textContent = `-${this.formatMoney(totals.discount)}`;
      }
      if (this.els.mobileTotal) {
        this.els.mobileTotal.textContent = this.formatMoney(totals.total);
      }

      // Savings message
      if (this.els.mobileSavings) {
        if (totals.qualifies && totals.discount > 0) {
          this.els.mobileSavings.textContent = this.t('savedMessage', { amount: this.formatMoney(totals.discount) });
          this.els.mobileSavings.style.display = '';
        } else {
          this.els.mobileSavings.style.display = 'none';
        }
      }
    }

    updateCTAState() {
      const canAdd = this.canAddToCart();
      if (this.els.ctaBtn) this.els.ctaBtn.disabled = !canAdd;
      if (this.els.mobileCta) this.els.mobileCta.disabled = !canAdd;

      // Update CTA hint for disabled state
      const remaining = this.config.bundleMin - this.getTotalQuantity();
      const hintText = canAdd ? '' : this.t('ctaHint', { remaining });
      const desktopHint = this.element.querySelector('[data-cta-hint]');
      const mobileHint = this.element.querySelector('[data-mobile-cta-hint]');
      if (desktopHint) desktopHint.textContent = hintText;
      if (mobileHint) mobileHint.textContent = hintText;
    }

    // ─── Calculations ──────────────────────────────────────────

    calculateTotals() {
      let subtotal = 0;
      this.state.selectedProducts.forEach(sel => {
        subtotal += sel.price * (sel.quantity || 1);
      });

      const qualifies = this.getTotalQuantity() >= this.config.bundleMin;
      const discountRate = this.config.discountPercentage / 100;
      const discount = qualifies ? Math.round(subtotal * discountRate) : 0;
      const total = subtotal - discount;

      return { subtotal, discount, total, qualifies };
    }

    canAddToCart() {
      const count = this.getTotalQuantity();
      return count >= this.config.bundleMin && count <= this.config.bundleMax;
    }

    getTotalQuantity() {
      let total = 0;
      this.state.selectedProducts.forEach(sel => { total += (sel.quantity || 1); });
      return total;
    }

    getStepCount(stepIndex) {
      let count = 0;
      this.state.selectedProducts.forEach(sel => {
        if (sel.stepIndex === stepIndex) count += (sel.quantity || 1);
      });
      return count;
    }

    getRemainingCapacity(stepIndex) {
      const step = this.config.steps[stepIndex];
      const stepRemaining = step ? step.capacity - this.getStepCount(stepIndex) : Infinity;
      const bundleRemaining = this.config.bundleMax - this.getTotalQuantity();
      return Math.max(0, Math.min(stepRemaining, bundleRemaining));
    }

    findSelectionKey(productId, stepIndex) {
      for (const [key, sel] of this.state.selectedProducts) {
        if (sel.productId === productId && sel.stepIndex === stepIndex) return key;
      }
      return null;
    }

    findSelectionKeyByVariant(variantId, stepIndex) {
      for (const [key, sel] of this.state.selectedProducts) {
        if (sel.variantId === variantId && sel.stepIndex === stepIndex) return key;
      }
      return null;
    }

    // ─── Cart integration ──────────────────────────────────────

    async addToCart() {
      if (this.state.isLoading) return;

      this.setLoading(true);

      try {
        const items = this.buildCartItems();

        const response = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({items, sections: 'mini-cart' })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || this.t('cartError'));
        }

        const responseJson = await response.json();

        // Dispatch Bohomoon cart events
        const cartResponse = await fetch(this.config.cartUrl);
        if (!cartResponse.ok) throw new Error(this.t('cartRefreshError'));
        const cartContent = await cartResponse.json();

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartContent }
          })
        );
        // window.dispatchEvent(
        //   new CustomEvent('venn:payload', {
        //     bubbles: true,
        //     detail: {
        //       variant: cartContent.items,
        //     },
        //   })
        // )

        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: true
            }
          })
        );

        this.announce(this.t('cartAdded'));

      } catch (error) {
        this.announce(error.message || this.t('cartError'));
      } finally {
        this.setLoading(false);
      }
    }

    buildCartItems() {
      const itemMap = new Map();
      const bundleId = self.crypto.randomUUID();

      this.state.selectedProducts.forEach(selection => {
        const key = selection.variantId;
        const qty = selection.quantity || 1;
        if (itemMap.has(key)) {
          itemMap.get(key).quantity += qty;
        } else {
          itemMap.set(key, {
            id: selection.variantId,
            quantity: qty,
            properties: {
              [this.config.bundleTagKey]: this.config.bundleTagValue,
              __venn_bundle_id: bundleId,
            },
          })
        }
      });
      return Array.from(itemMap.values());
    }

    // ─── Helpers ───────────────────────────────────────────────

    /**
     * Get a translated string, with optional placeholder substitution.
     * Falls back to the key name if no translation is found.
     * @param {string} key - Translation key from config.translations
     * @param {Object} [replacements] - Key/value pairs to substitute {{ key }} placeholders
     */
    t(key, replacements) {
      let str = this.config.translations[key] || key;
      if (replacements) {
        Object.entries(replacements).forEach(([k, v]) => {
          str = str.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
        });
      }
      return str;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    getIconHtml(name) {
      const tpl = document.querySelector(`template[data-bb-icon="${name}"]`);
      return tpl ? tpl.innerHTML : '';
    }

    parseColorSwatchConfig(configArray) {
      const map = new Map();
      configArray.forEach(entry => {
        const parts = entry.split(':');
        if (parts.length >= 2) {
          const name = parts[0].trim().toLowerCase();
          const value = parts.slice(1).join(':').trim();
          map.set(name, value);
        }
      });
      return map;
    }

    getSwatchStyle(label) {
      const key = label.toLowerCase();
      const mapped = this.config.colorSwatchConfig.get(key);
      if (mapped && mapped.startsWith('#')) {
        return `background-color: ${mapped}`;
      } else if (mapped && mapped.startsWith('image(')) {
        const url = mapped.slice(6, -1);
        return `background-image: url(${url}); background-size: cover`;
      }
      return `background-color: ${key}`;
    }

    /**
     * Append a width parameter to a Shopify CDN image URL.
     * Handles both modern (?width=) and legacy (/files/) URL formats.
     */
    sizedImageUrl(url, width) {
      if (!url) return url;
      return url.includes('?')
        ? `${url}&width=${width}`
        : `${url}?width=${width}`;
    }

    getVariantData(card) {
      try {
        return JSON.parse(card.dataset.variantData || '[]');
      } catch {
        return [];
      }
    }

    formatMoney(cents) {
      if (!cents || isNaN(cents)) return '£0.00';
      const amount = parseInt(cents) || 0;

      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(amount, this.config.moneyFormat);
      }

      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(amount / 100);
    }

    setLoading(loading) {
      this.state.isLoading = loading;
      this.element.classList.toggle('is-loading', loading);

      if (this.els.ctaBtn) {
        this.els.ctaBtn.disabled = loading;
        this.els.ctaBtn.setAttribute('aria-busy', loading);
      }
      if (this.els.mobileCta) {
        this.els.mobileCta.disabled = loading;
        this.els.mobileCta.setAttribute('aria-busy', loading);
      }
    }

    announce(message) {
      if (this.els.announcement) {
        this.els.announcement.textContent = message;
      }
    }
  }

  // ─── Initialise ────────────────────────────────────────────

  function initBundleBuilder() {
    const el = document.getElementById('bundle-builder');
    if (el && !el._bundleBuilder) {
      el._bundleBuilder = new BundleBuilder(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleBuilder);
  } else {
    initBundleBuilder();
  }

  // Shopify section lifecycle
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#bundle-builder')) {
      initBundleBuilder();
    }
  });
})();
